export interface GridSettings {
    enabled: boolean;
    sizeMeters: number;
}

const EARTH_RADIUS = 6371000;

export function metersToLatitude(meters: number): number {
    return meters / 111139;
}

export function metersToLongitude(meters: number, latitude: number): number {
    const latRad = (latitude * Math.PI) / 180;
    return meters / (111139 * Math.cos(latRad));
}

export function latitudeToMeters(degrees: number): number {
    return degrees * 111139;
}

export function longitudeToMeters(degrees: number, latitude: number): number {
    const latRad = (latitude * Math.PI) / 180;
    return degrees * (111139 * Math.cos(latRad));
}

export function snapToGrid(
    latLng: google.maps.LatLng,
    gridSettings: GridSettings,
    referencePoint?: google.maps.LatLng
): google.maps.LatLng {
    if (!gridSettings.enabled) {
        return latLng;
    }

    const lat = latLng.lat();
    const lng = latLng.lng();

    const origin = referencePoint || latLng;
    const originLat = origin.lat();
    const originLng = origin.lng();

    const latOffsetMeters = latitudeToMeters(lat - originLat);
    const lngOffsetMeters = longitudeToMeters(lng - originLng, originLat);

    const gridSize = gridSettings.sizeMeters;
    const snappedLatOffsetMeters = Math.round(latOffsetMeters / gridSize) * gridSize;
    const snappedLngOffsetMeters = Math.round(lngOffsetMeters / gridSize) * gridSize;

    const snappedLat = originLat + metersToLatitude(snappedLatOffsetMeters);
    const snappedLng = originLng + metersToLongitude(snappedLngOffsetMeters, originLat);

    return new google.maps.LatLng(snappedLat, snappedLng);
}

export interface GridLine {
    path: google.maps.LatLng[];
    type: 'latitude' | 'longitude';
}

export function generateGridLines(
    bounds: google.maps.LatLngBounds,
    gridSettings: GridSettings,
    referencePoint?: google.maps.LatLng
): GridLine[] {
    if (!gridSettings.enabled) {
        return [];
    }

    const lines: GridLine[] = [];
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const origin = referencePoint || bounds.getCenter();
    const originLat = origin.lat();
    const originLng = origin.lng();

    const gridSize = gridSettings.sizeMeters;

    const northMeters = latitudeToMeters(ne.lat() - originLat);
    const southMeters = latitudeToMeters(sw.lat() - originLat);
    const eastMeters = longitudeToMeters(ne.lng() - originLng, originLat);
    const westMeters = longitudeToMeters(sw.lng() - originLng, originLat);

    const startLatMeters = Math.floor(southMeters / gridSize) * gridSize;
    const endLatMeters = Math.ceil(northMeters / gridSize) * gridSize;

    for (let latMeters = startLatMeters; latMeters <= endLatMeters; latMeters += gridSize) {
        const lat = originLat + metersToLatitude(latMeters);
        lines.push({
            type: 'latitude',
            path: [
                new google.maps.LatLng(lat, sw.lng()),
                new google.maps.LatLng(lat, ne.lng()),
            ],
        });
    }

    const startLngMeters = Math.floor(westMeters / gridSize) * gridSize;
    const endLngMeters = Math.ceil(eastMeters / gridSize) * gridSize;

    for (let lngMeters = startLngMeters; lngMeters <= endLngMeters; lngMeters += gridSize) {
        const lng = originLng + metersToLongitude(lngMeters, originLat);
        lines.push({
            type: 'longitude',
            path: [
                new google.maps.LatLng(sw.lat(), lng),
                new google.maps.LatLng(ne.lat(), lng),
            ],
        });
    }

    return lines;
}

export function calculateDistance(point1: google.maps.LatLng, point2: google.maps.LatLng): number {
    const lat1 = (point1.lat() * Math.PI) / 180;
    const lat2 = (point2.lat() * Math.PI) / 180;
    const deltaLat = lat2 - lat1;
    const deltaLng = ((point2.lng() - point1.lng()) * Math.PI) / 180;

    const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS * c;
}

export function calculateBearing(point1: google.maps.LatLng, point2: google.maps.LatLng): number {
    const lat1 = (point1.lat() * Math.PI) / 180;
    const lat2 = (point2.lat() * Math.PI) / 180;
    const deltaLng = ((point2.lng() - point1.lng()) * Math.PI) / 180;

    const y = Math.sin(deltaLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);

    let bearing = (Math.atan2(y, x) * 180) / Math.PI;
    bearing = (bearing + 360) % 360;

    return bearing;
}
